import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UpcomingMatchCard = ({ match, onAttendanceChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })?.format(date);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'cancelled': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'cancelled': return 'XCircle';
      default: return 'Calendar';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendente';
      case 'cancelled': return 'Cancelada';
      default: return 'Agendada';
    }
  };

  const handleAttendanceConfirm = () => {
    onAttendanceChange(match?.id, 'confirmed');
  };

  const handleAttendanceCancel = () => {
    onAttendanceChange(match?.id, 'cancelled');
  };

  const handleViewDetails = () => {
    navigate('/match-details', { state: { matchId: match?.id } });
  };

  const attendancePercentage = (match?.attendees?.confirmed / match?.attendees?.total) * 100;
  const paymentPercentage = (match?.payment?.paid / match?.payment?.total) * 100;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden transition-standard hover:shadow-elevation-3">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-card-foreground mb-1">
              {match?.title}
            </h3>
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="MapPin" size={16} />
              <span className="text-sm">{match?.venue}</span>
            </div>
            <div className="flex items-center space-x-2 text-muted-foreground mt-1">
              <Icon name="Calendar" size={16} />
              <span className="text-sm font-data">
                {formatDate(match?.date)} • {match?.time}
              </span>
            </div>
          </div>
          
          <div className={`flex items-center space-x-1 ${getStatusColor(match?.status)}`}>
            <Icon name={getStatusIcon(match?.status)} size={16} />
            <span className="text-sm font-medium">
              {getStatusText(match?.status)}
            </span>
          </div>
        </div>

        {/* Weather Info */}
        {match?.weather && (
          <div className="flex items-center space-x-2 bg-muted/50 rounded-lg p-2 mb-3">
            <Icon name={match?.weather?.icon} size={16} className="text-primary" />
            <span className="text-sm text-muted-foreground">
              {match?.weather?.temperature}°C • {match?.weather?.condition}
            </span>
            {match?.weather?.rainChance > 30 && (
              <div className="flex items-center space-x-1 text-warning">
                <Icon name="CloudRain" size={14} />
                <span className="text-xs">{match?.weather?.rainChance}%</span>
              </div>
            )}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Jogadores</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-sm font-medium text-success">
                {match?.attendees?.confirmed}
              </span>
              <span className="text-sm text-muted-foreground">/</span>
              <span className="text-sm text-muted-foreground">
                {match?.attendees?.total}
              </span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="CreditCard" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Valor</span>
            </div>
            <span className="text-sm font-data font-medium text-card-foreground">
              {formatCurrency(match?.payment?.amount)}
            </span>
          </div>
        </div>

        {/* Progress Bars */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Confirmações</span>
            <span>{Math.round(attendancePercentage)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full transition-all duration-300"
              style={{ width: `${attendancePercentage}%` }}
            />
          </div>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Pagamentos</span>
            <span>{Math.round(paymentPercentage)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${paymentPercentage}%` }}
            />
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="p-4">
        {match?.userStatus === 'pending' ? (
          <div className="flex space-x-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleAttendanceConfirm}
              iconName="Check"
              iconPosition="left"
              iconSize={16}
              className="flex-1"
            >
              Confirmar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAttendanceCancel}
              iconName="X"
              iconPosition="left"
              iconSize={16}
              className="flex-1"
            >
              Recusar
            </Button>
          </div>
        ) : (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewDetails}
              iconName="Eye"
              iconPosition="left"
              iconSize={16}
              className="flex-1"
            >
              Ver Detalhes
            </Button>
            {match?.userStatus === 'confirmed' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                iconPosition="right"
                iconSize={16}
              >
                {isExpanded ? 'Menos' : 'Mais'}
              </Button>
            )}
          </div>
        )}

        {/* Expanded Details */}
        {isExpanded && match?.userStatus === 'confirmed' && (
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            {/* Confirmed Players */}
            <div>
              <h4 className="text-sm font-medium text-card-foreground mb-2">
                Jogadores Confirmados
              </h4>
              <div className="flex flex-wrap gap-2">
                {match?.confirmedPlayers?.slice(0, 6)?.map((player, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-muted/50 rounded-lg px-2 py-1">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      {player?.avatar ? (
                        <Image 
                          src={player?.avatar} 
                          alt={player?.avatarAlt}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <Icon name="User" size={12} color="white" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {player?.name}
                    </span>
                  </div>
                ))}
                {match?.confirmedPlayers?.length > 6 && (
                  <div className="flex items-center justify-center w-6 h-6 bg-muted rounded-full">
                    <span className="text-xs text-muted-foreground">
                      +{match?.confirmedPlayers?.length - 6}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => console.log('Opening chat...')}
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={16}
                className="flex-1"
              >
                Chat
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/barbecue-planning', { state: { matchId: match?.id } })}
                iconName="Utensils"
                iconPosition="left"
                iconSize={16}
                className="flex-1"
              >
                Churrasco
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingMatchCard;